import { useEffect, useState } from "react";
import { useDataProvider, useNotify, type RaRecord } from "react-admin";

import { fetchListAll } from "./fetchListAll";
import { DEFAULT_MAX_RECORDS } from "./paginationLimits";

export interface UseGetListAllParams {
  filter?: Record<string, unknown>;
  sort?: { field: string; order: "ASC" | "DESC" };
  /** Upper bound on rows fetched. Defaults to DEFAULT_MAX_RECORDS. */
  maxRecords?: number;
}

export interface UseGetListAllResult<RecordType extends RaRecord = RaRecord> {
  data: RecordType[];
  total: number;
  isLoading: boolean;
  truncated: boolean;
  error?: unknown;
}

/**
 * Hook wrapper around fetchListAll for components that need the *entire* list
 * (e.g. a lookup/name map), not one page. Replaces the `useGetList(..., { perPage:
 * 10000 })` anti-pattern: that silently returns only the first server page once
 * the backend page-size cap is enforced (#5166). Pages through 1000-row chunks
 * and warns loudly if the set is truncated at maxRecords.
 *
 * filter/sort are compared by value (serialized) so passing fresh object
 * literals each render does not re-fetch or loop.
 */
export function useGetListAll<RecordType extends RaRecord = RaRecord>(
  resource: string,
  params: UseGetListAllParams = {},
  options: { enabled?: boolean } = {}
): UseGetListAllResult<RecordType> {
  const { enabled = true } = options;
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [state, setState] = useState<UseGetListAllResult<RecordType>>({
    data: [],
    total: 0,
    isLoading: enabled,
    truncated: false,
  });

  const { filter, sort, maxRecords = DEFAULT_MAX_RECORDS } = params;
  // Stable dependency: identity-independent key over the query inputs.
  const paramsKey = JSON.stringify({ resource, filter: filter ?? null, sort: sort ?? null, maxRecords, enabled });

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }
    let cancelled = false;
    setState((s) => ({ ...s, isLoading: true }));
    fetchListAll<RecordType>(
      dataProvider,
      resource,
      { filter, sort },
      {
        maxRecords,
        onTruncated: (fetched, total) =>
          notify(`Showing first ${fetched} of ${total} ${resource} — narrow the view for the full set`, {
            type: "warning",
          }),
      }
    )
      .then((res) => {
        if (!cancelled) setState({ data: res.data, total: res.total, isLoading: false, truncated: res.truncated });
      })
      .catch((error) => {
        if (!cancelled) setState((s) => ({ ...s, isLoading: false, error }));
      });
    return () => {
      cancelled = true;
    };
    // filter/sort intentionally tracked via paramsKey to avoid ref-identity loops.
  }, [paramsKey, dataProvider, notify]);

  return state;
}
